import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormControl } from 'react-bootstrap';

const toDraftString = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? String(numberValue) : '';
};

/**
 * Numeric input that keeps the literal string being edited locally.
 *
 * This lets a user clear the field without React immediately coercing the
 * empty value to zero. Finite values are committed as they are typed; on blur
 * the draft resynchronizes with the normalized parent value.
 */
function NumberCellInput({
  id,
  value,
  disabled,
  size,
  min,
  className,
  skipEmptyCommit,
  onCommit,
}) {
  const [draft, setDraft] = useState(() => toDraftString(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(toDraftString(value));
  }, [value]);

  const handleChange = (event) => {
    const raw = event.target.value;
    setDraft(raw);
    if (raw === '') {
      if (!skipEmptyCommit) onCommit(undefined);
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) onCommit(parsed);
  };

  return (
    <FormControl
      id={id}
      type="number"
      size={size}
      min={min}
      className={className}
      value={draft}
      disabled={disabled}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
        setDraft(toDraftString(value));
      }}
      onChange={handleChange}
    />
  );
}

NumberCellInput.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
  size: PropTypes.string,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  skipEmptyCommit: PropTypes.bool,
  onCommit: PropTypes.func.isRequired,
};

NumberCellInput.defaultProps = {
  value: '',
  disabled: false,
  size: 'sm',
  min: undefined,
  className: undefined,
  skipEmptyCommit: false,
};

export default NumberCellInput;
